import React from 'react';
import { ExternalLinkIcon } from '@nebula/icons/ui';
import uiIconSizes from '@nebula/icons/ui/iconSizes';
import nebulaTheme from '@nebula/theme';
import { CTAButton, VoucherButton, CTAStack, CurrentLabel } from './index.styles';
import type { PlanId, CtaAction } from '../../types';

const PLAN_DISPLAY_NAMES: Record<PlanId, string> = {
  basic: 'Free account',
  identity: 'Identity Plus',
  credit_expert: 'CreditExpert',
};

export const getPlanAction = (currentPlanId: PlanId, planId: PlanId, product?: any): CtaAction => {
  if (currentPlanId === planId) {
    return planId === 'basic' ? { type: 'current' } : { type: 'manage' };
  }
  if (product?.trialLength) {
    return {
      type: 'action',
      label: 'Start 30 day free trial',
      ariaLabel: `Start a 30 day free trial of ${PLAN_DISPLAY_NAMES[planId]}.`,
    };
  }

  const verb = currentPlanId === 'basic' ? 'Upgrade' : 'Switch';
  return { type: 'action', label: `${verb} to ${PLAN_DISPLAY_NAMES[planId]}` };
};

interface PlanActionProps {
  currentPlanId: PlanId;
  planId: PlanId;
  switchPermitted: boolean;
  product?: any;
  onSelectPlan?: () => void;
  onManage?: () => void;
  onVoucher?: () => void;
}

const PlanAction: React.FC<PlanActionProps> = ({
  currentPlanId,
  planId,
  switchPermitted,
  product,
  onSelectPlan,
  onManage,
  onVoucher,
}) => {
  const cta = getPlanAction(currentPlanId, planId, product);

  if (cta.type === 'current') {
    return <CurrentLabel>This is your current account.</CurrentLabel>;
  }

  if (cta.type === 'manage') {
    return <CTAButton buttonKind="secondaryMagenta" label="Manage subscription" onClick={onManage} isButton />;
  }

  const showVoucher = currentPlanId === 'basic' && planId === 'identity';

  return (
    <CTAStack>
      <CTAButton
        buttonKind="primaryMagenta"
        label={cta.label}
        aria-label={cta.ariaLabel}
        disabled={!switchPermitted}
        onClick={onSelectPlan}
        isButton
      />
      {showVoucher && (
        <VoucherButton
          buttonKind="secondaryMagenta"
          label="Enter a voucher"
          onClick={onVoucher}
          icon={<ExternalLinkIcon iconColor={nebulaTheme.color.decorative.magenta[70]} iconSize={uiIconSizes.xSmall} />}
        />
      )}
    </CTAStack>
  );
};

export default PlanAction;
